/**
 * The plan rules of the cover editor (spec `2026-10-08-music24-cover-editor-design.md` §4, owner
 * decisions D5, D6, D7). The table is outside the capabilities object, like
 * `MUSIC24_VIDEO_QUALITY`, so the native plan parity tests stay green.
 */
import type { Music24PlanTier } from './music24Plans';

/** One AI run makes this many pictures. */
export const COVER_AI_RUN_SIZE = 4;

/**
 * The AI pictures of a workspace in a month (D5). Free and the trial have no AI: they use the bank.
 * The numbers are the owner's (2026-10-08): the cover AI stays near 15–20 % of the plan price (at
 * about 0.15 USD a picture: Artist 19 USD → 3, Pro 39 → 6, Manager 69 → 15, Label 249 → 45 USD).
 * One run makes 4 pictures, so every number is a whole number of runs.
 */
export const MUSIC24_COVER_AI_PICTURES: Readonly<Record<Music24PlanTier, number>> = {
  free: 0,
  trial: 0,
  artist: 20,
  pro: 40,
  // Plug is the Pro plan plus Outreach: the Pro number.
  plug: 40,
  manager: 100,
  label: 300,
};

/** The AI pictures of the plan in a month; 0 for a plan with no AI. */
export function coverAiLimit(plan: Music24PlanTier): number {
  return MUSIC24_COVER_AI_PICTURES[plan];
}

/** True when the plan can make AI pictures. */
export function canUseCoverAi(plan: Music24PlanTier): boolean {
  return coverAiLimit(plan) > 0;
}

/**
 * The welcome run (owner request 2026-10-09: "the first 4 images are on us"): the first AI pictures
 * of a workspace on this plan cost no quota. Only the trial (new signups) has one. One run per
 * workspace; a run whose 4 pictures all failed does not count (music24-api `coverWelcome.ts`).
 */
export const MUSIC24_COVER_WELCOME_PICTURES: Readonly<Record<Music24PlanTier, number>> = {
  free: 0,
  trial: COVER_AI_RUN_SIZE,
  artist: 0,
  pro: 0,
  plug: 0,
  manager: 0,
  label: 0,
};

/** True when the plan has a welcome run of AI pictures (on us, once per workspace). */
export function hasCoverWelcomeRun(plan: Music24PlanTier): boolean {
  return MUSIC24_COVER_WELCOME_PICTURES[plan] > 0;
}

/**
 * True when the editor always shows the card "Get a picture made for your song" (owner request
 * 2026-10-09): a plan with no AI and no welcome run, so the Free plan. A paid plan and the trial
 * never see it pinned.
 */
export function coverAiUpsellAlwaysShown(plan: Music24PlanTier): boolean {
  return !canUseCoverAi(plan) && !hasCoverWelcomeRun(plan);
}

/**
 * True when the download of the plan carries the Music24 watermark (D7): Free and the trial. The
 * public tool (no account) always has it.
 */
export function coverHasWatermark(plan: Music24PlanTier | null): boolean {
  return plan === null || plan === 'free' || plan === 'trial';
}

/** How many full runs the quota still pays for. */
export function coverAiRunsLeft(quota: { used: number; limit: number }): number {
  return Math.max(0, Math.floor((quota.limit - quota.used) / COVER_AI_RUN_SIZE));
}
